import {Nullable, Options} from '@fl/cmsch-fe-library';
import {none, Opt} from 'ts-opt';

import {AuthDiscriminatorDetail} from 'api/gen/AuthDiscriminatorDetail';
import {BasicRole} from 'api/gen/BasicRole';
import {ColumnHeader} from 'api/gen/ColumnHeader';
import {Farm} from 'api/gen/Farm';
import {OrganizationCenter} from 'api/gen/OrganizationCenter';
import {PagedModelAssociationRole} from 'api/gen/PagedModelAssociationRole';
import {PagedModelBasicRole} from 'api/gen/PagedModelBasicRole';
import {PagedModelBreederRole} from 'api/gen/PagedModelBreederRole';
import {PagedModelOrganizationRole} from 'api/gen/PagedModelOrganizationRole';
import {PagedModelOtherSubjectRole} from 'api/gen/PagedModelOtherSubjectRole';
import {PagedModelPermissionDetail} from 'api/gen/PagedModelPermissionDetail';
import {PagedModelRolePermission} from 'api/gen/PagedModelRolePermission';
import {PagedModelRoleView} from 'api/gen/PagedModelRoleView';
import {PagedModelUserProfileContact} from 'api/gen/PagedModelUserProfileContact';
import {PagedModelUserSubjectView} from 'api/gen/PagedModelUserSubjectView';
import {PagedModelUserView} from 'api/gen/PagedModelUserView';
import {RoleDetail} from 'api/gen/RoleDetail';
import {User} from 'api/gen/User';
import {UserDetail} from 'api/gen/UserDetail';
import {UserProfileAssociationRole} from 'api/gen/UserProfileAssociationRole';
import {UserProfileBreederRoleFarms} from 'api/gen/UserProfileBreederRoleFarms';
import {UserProfileOrganizationRoleFarmsCenters} from 'api/gen/UserProfileOrganizationRoleFarmsCenters';
import {UserProfileOtherSubjectRole} from 'api/gen/UserProfileOtherSubjectRole';

import {AdminLoadingId} from '../types/admin-loading-id';
import {AppInfoType} from '../types/app-info-type';
import {ManagementAssociationsPage} from '../types/management-associations';
import {ManagementBreedersPage} from '../types/management-breeders';
import {ManagementOrganizationsPage} from '../types/management-organizations';
import {ManagementOtherSubjectsPage} from '../types/management-other-subjects';

/** Redux state for the admin module. */
export interface AdminState {
    /** Loaded application info for the admin app-info screen. */
    appInfo: Opt<AppInfoType>;
    /** Loaded roles list for the roles screen. */
    rolesPage: Opt<PagedModelRoleView>;
    /** Whether the role-create modal is visible. */
    postRoleModalVisible: boolean;
    /** Role id currently loaded into the edit modal. */
    editRoleModalData: Opt<string>;
    /** Loaded role detail. */
    roleDetail: Opt<RoleDetail>;
    /** Loaded permissions assigned to the selected role. */
    rolePermissionsPage: Opt<PagedModelRolePermission>;
    /** Loaded breeder-management table data. */
    managementBreedersPage: Opt<ManagementBreedersPage>;
    /** Breeder-management select options. */
    managementBreedersOptions: Options<number>;
    /** Extra breeder-management table columns. */
    managementBreedersExtraColumns: Array<ColumnHeader>;
    /** Farm rows available to breeder management. */
    managementBreedersFarms: Array<Farm>;
    /** Loaded breeder-management detail. */
    managementBreederDetail: Opt<UserProfileBreederRoleFarms>;
    /** Loaded permissions that are not yet assigned to the selected role. */
    roleUnassignedPermissionsPage: Opt<PagedModelRolePermission>;
    /** Loaded permission detail rows. */
    permissionDetailsPage: Opt<PagedModelPermissionDetail>;
    /** Permission id currently being edited. */
    permissionIdInEdit: number | null;
    /** Role id currently being edited. */
    roleIdInEdit: number | null;
    /** Whether the permission-create modal is visible. */
    permissionCreateVisible: boolean;
    /** Whether the role-create modal is visible. */
    roleCreateModalVisible: boolean;
    /** Assigned subordinate roles for the selected role. */
    subordinateAssignedRolePage: Opt<PagedModelBasicRole>;
    /** Available subordinate roles for the selected role. */
    subordinateUnassignedRolePage: Opt<PagedModelBasicRole>;
    /** Loaded users list for the users screen. */
    usersPage: Opt<PagedModelUserView>;
    /** Loaded user detail. */
    userDetail: Opt<UserDetail>;
    /** Membership rows for the selected user. */
    memberOfSubjects: Opt<PagedModelUserSubjectView>;
    /** Contact rows for the selected user. */
    userContacts: Opt<PagedModelUserProfileContact>;
    /** Association roles for the selected user. */
    userAssociations: Opt<PagedModelAssociationRole>;
    /** Other-subject roles for the selected user. */
    userOtherSubjectRoles: Opt<PagedModelOtherSubjectRole>;
    /** Organization roles for the selected user. */
    organizations: Opt<PagedModelOrganizationRole>;
    /** Breeder roles for the selected user. */
    userBreeders: Opt<PagedModelBreederRole>;
    /** Identifiers of currently loading admin requests. */
    loading: Array<AdminLoadingId>;
    /** Visible modal key for management subject flows. */
    managementSubjectModalVisible: Nullable<string>;
    /** Visible modal key for management association flows. */
    subjectManagementModalVisible: Nullable<string>;
    /** Available breeder roles for management forms. */
    managementBreedersRoles: Array<BasicRole>;
    /** Available breeder users for management forms. */
    managementBreedersUsers: Array<User>;
    /** Available breeder farm rows for management forms. */
    managementBreedersUserFarms: Array<Farm>;

    /** Available association options for management forms. */
    managementAssociationsOptions: Options<number>;
    /** Loaded association-management table data. */
    managementAssociationsPage: Opt<ManagementAssociationsPage>;
    /** Extra association-management table columns. */
    managementAssociationsExtraColumns: Array<ColumnHeader>;
    /** Available association users for management forms. */
    managementAssociationsUsers: Array<User>;
    /** Available association roles for management forms. */
    managementAssociationsRoles: Array<BasicRole>;
    /** Loaded association-management detail. */
    managementAssociationDetail: Opt<UserProfileAssociationRole>;

    /** Available discriminator rows for admin forms. */
    adminDiscriminators: Array<AuthDiscriminatorDetail>;

    /** Available other-subject options for management forms. */
    managementOtherSubjectsOptions: Options<number>;
    /** Loaded other-subject-management table data. */
    managementOtherSubjectsPage: Opt<ManagementOtherSubjectsPage>;
    /** Extra other-subject-management table columns. */
    managementOtherSubjectsExtraColumns: Array<ColumnHeader>;
    /** Available other-subject users for management forms. */
    managementOtherSubjectsUsers: Array<User>;
    /** Available other-subject roles for management forms. */
    managementOtherSubjectsRoles: Array<BasicRole>;
    /** Loaded other-subject-management detail. */
    managementOtherSubjectDetail: Opt<UserProfileOtherSubjectRole>;

    /** Available organization options for management forms. */
    managementOrganizationsOptions: Options<number>;
    /** Loaded organization-management table data. */
    managementOrganizationsPage: Opt<ManagementOrganizationsPage>;
    /** Extra organization-management table columns. */
    managementOrganizationsExtraColumns: Array<ColumnHeader>;
    /** Available organization users for management forms. */
    managementOrganizationsUsers: Array<User>;
    /** Available organization roles for management forms. */
    managementOrganizationsRoles: Array<BasicRole>;
    /** Loaded organization-management detail. */
    managementOrganizationDetail: Opt<UserProfileOrganizationRoleFarmsCenters>;
    /** Farm rows available to organization management. */
    managementOrganizationFarms: Array<Farm>;
    /** Center rows available to organization management. */
    managementOrganizationCenters: Array<OrganizationCenter>;
}

/** Initial Redux state for the admin module. */
export const initialAdminState: AdminState = {
    appInfo: none,
    rolesPage: none,
    postRoleModalVisible: false,
    organizations: none,
    editRoleModalData: none,
    managementBreederDetail: none,
    managementBreedersPage: none,
    managementBreedersOptions: [],
    managementBreedersExtraColumns: [],
    managementBreedersFarms: [],
    roleDetail: none,
    rolePermissionsPage: none,
    roleUnassignedPermissionsPage: none,
    permissionDetailsPage: none,
    permissionIdInEdit: null,
    roleIdInEdit: null,
    permissionCreateVisible: false,
    roleCreateModalVisible: false,
    subordinateAssignedRolePage: none,
    subordinateUnassignedRolePage: none,
    usersPage: none,
    userDetail: none,
    memberOfSubjects: none,
    userContacts: none,
    userAssociations: none,
    userOtherSubjectRoles: none,
    userBreeders: none,
    loading: [],
    subjectManagementModalVisible: null,
    managementBreedersRoles: [],
    managementBreedersUsers: [],
    managementBreedersUserFarms: [],
    managementSubjectModalVisible: null,
    adminDiscriminators: [],

    managementAssociationsOptions: [],
    managementAssociationsPage: none,
    managementAssociationsExtraColumns: [],
    managementAssociationsUsers: [],
    managementAssociationsRoles: [],
    managementAssociationDetail: none,

    managementOtherSubjectsOptions: [],
    managementOtherSubjectsPage: none,
    managementOtherSubjectsExtraColumns: [],
    managementOtherSubjectsUsers: [],
    managementOtherSubjectsRoles: [],
    managementOtherSubjectDetail: none,

    managementOrganizationsOptions: [],
    managementOrganizationsPage: none,
    managementOrganizationsExtraColumns: [],
    managementOrganizationsUsers: [],
    managementOrganizationsRoles: [],
    managementOrganizationDetail: none,
    managementOrganizationFarms: [],
    managementOrganizationCenters: [],
};
